// Welle 4 — Geo-Integrity queue ("3 listings outside Croatia → review").
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Globe2 } from "lucide-react";
import { fetchGeoSuspects } from "../../../api/adminDashboardApi";
import { SectionCard, EmptyHint, Pill } from "./AdminV2Kit";

// Welle 4.1 — clear repair classification per reason: swapped
// coordinates can be fixed automatically (flip lat/lng); everything
// else needs a human look.
const AUTO_FIXABLE = new Set(["geo_swapped_latlng"]);

function RepairTag({ reason, t }) {
  const auto = AUTO_FIXABLE.has(reason);
  return (
    <Pill tone={auto ? "emerald" : "amber"}>
      {auto ? t("modules.geo_queue.autoFixable") : t("modules.geo_queue.manualReview")}
    </Pill>
  );
}

export default function GeoQueueCard({ summary, developer = false }) {
  const { t } = useTranslation("admin");
  const total = summary?.total ?? 0;
  const byReason = summary?.by_reason || {};
  const byCountry = summary?.by_country || {};
  const [items, setItems] = useState([]);

  useEffect(() => {
    let alive = true;
    if (total > 0) {
      Promise.all([fetchGeoSuspects("immo", 20), fetchGeoSuspects("auto", 20)])
        .then(([immo, auto]) =>
          alive && setItems([...(immo.items || []), ...(auto.items || [])]))
        .catch(() => {});
    } else {
      setItems([]);
    }
    return () => { alive = false; };
  }, [total]);

  return (
    <SectionCard
      title={t("modules.geo_queue.title")}
      icon={Globe2}
      status={total > 0 ? "attention" : "healthy"}
      testid="adminv2-geo-card"
      actions={
        <div className="text-2xl font-semibold text-zinc-100 tabular-nums">{total}</div>
      }
    >
      {total === 0 ? (
        <EmptyHint testid="adminv2-geo-empty">{t("modules.geo_queue.empty")}</EmptyHint>
      ) : (
        <>
          <div
            data-testid="adminv2-geo-summary"
            className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3.5 py-2.5 text-sm text-amber-200"
          >
            {t("modules.geo_queue.summary", { count: total })}
            <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
              {Object.entries(byCountry).map(([c, n]) => (
                <Pill key={c} tone="amber">{c}: {n}</Pill>
              ))}
            </span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {Object.entries(byReason).map(([r, n]) => (
              <span key={r} className="inline-flex items-center gap-1">
                <Pill tone="zinc">
                  {t(`modules.geo_queue.reasons.${r}`, { defaultValue: r })} · {n}
                </Pill>
                <RepairTag reason={r} t={t} />
              </span>
            ))}
          </div>
          <ul className="space-y-1.5">
            {items.slice(0, developer ? 20 : 5).map((it) => (
              <li
                key={it.ulid}
                data-testid={`adminv2-geo-item-${it.ulid}`}
                className="flex items-center justify-between gap-3 rounded-xl border border-zinc-800 bg-zinc-950/50 px-3.5 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm text-zinc-200">{it.title || it.ulid}</div>
                  <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-zinc-500">
                    <span>{it.country}</span>
                    <Pill tone="zinc">
                      {t(`modules.geo_queue.reasons.${it.geo_suspect_reason}`, {
                        defaultValue: it.geo_suspect_reason,
                      })}
                    </Pill>
                    <RepairTag reason={it.geo_suspect_reason} t={t} />
                    {developer && it.latitude != null && (
                      <span className="font-mono">
                        {Number(it.latitude).toFixed(3)}, {Number(it.longitude).toFixed(3)}
                      </span>
                    )}
                  </div>
                </div>
                <a
                  href={it.source_url || `#${it.ulid}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`adminv2-geo-review-${it.ulid}`}
                  className="shrink-0 rounded-full border border-zinc-700 px-2.5 py-1 text-[11px] text-zinc-300 hover:border-amber-500/50 hover:text-amber-300 transition-colors"
                >
                  {t("modules.geo_queue.review")}
                </a>
              </li>
            ))}
          </ul>
        </>
      )}
    </SectionCard>
  );
}
